<script lang="ts">
    import { useFragment } from '$gql/fragment-masking.js';
    import {
        FeedCommentFragmentDoc,
        FeedPostCommentsQueryDocument,
        type NewsFeedPostFragment,
    } from '$gql/graphql.js';
    import { query } from '$lib/apollo/SvelteApollo.js';
    import { NetworkStatus } from '@apollo/client/core/core.cjs';
    import map from 'lodash/map';
    import uniqBy from 'lodash/uniqBy';
    import {
        type CommentData,
        COMMENTS_MAX_PER_PAGE,
        findPage,
        INITIAL_COMMENTS_TO_SHOW,
    } from './Utils';
    import FeedCommentLoader from './FeedCommentLoader.svelte';
    import FeedComment from './FeedComment.svelte';
    import FeedCommentForm from './FeedCommentForm.svelte';
    import * as t from '$trans';
    import { getContainer } from '$components/Context/ContainerContext';
    import { participantToInput } from '$lib/utils/participantUtils.js';
    import { cx } from '$lib/utils/cx';

    const { currentUserStore, lang } = getContainer();
    const currentUser = currentUserStore().currentUser();

    export let post: NewsFeedPostFragment;
    export let commentData: CommentData | null = null;

    let showAllComments = commentData != null;

    let initialPage =
        commentData == null
            ? 1
            : commentData.parentIndex === null
              ? findPage(COMMENTS_MAX_PER_PAGE, commentData.commentIndex!)
              : findPage(COMMENTS_MAX_PER_PAGE, commentData.parentIndex!);

    const { result, setVariables, fetchMore } = query(FeedPostCommentsQueryDocument, {
        variables: {
            id: post.id,
            page: initialPage,
            maxPerPage: COMMENTS_MAX_PER_PAGE,
        },
    });
    $: setVariables({
        id: post.id,
        page: initialPage,
        maxPerPage: COMMENTS_MAX_PER_PAGE,
    });

    $: comments = map($result.data?.post?.comments?.results, (p) =>
        useFragment(FeedCommentFragmentDoc, p),
    );
    $: loading = $result.loading;
    $: currentPage = $result.data?.post.comments.pagerInfo.page || 1;
    $: nbPages = $result.data?.post.comments.pagerInfo.nbPages || 0;
    $: commentsCount = post.commentsCount;
</script>

<div class={cx(commentsCount > 0 ? 'pb-[12px]' : '')}>
    <!-- <CreateComment
        postId={post.id}
        onCreated={(comment) => {
            setTimeout(() => {
                document.getElementById(`FeedComment:${comment.id}`).scrollIntoView({behavior: "smooth"});
            }, 500);
        }}
    /> -->
    {#if $currentUser != null}
        <FeedCommentForm
            autoFocus={true}
            postId={post.id}
            initialAuthor={participantToInput($currentUser)}
        />
    {/if}

    {#if $result.networkStatus !== NetworkStatus.refetch}
        {#each showAllComments ? comments : comments.slice(0, INITIAL_COMMENTS_TO_SHOW) as comment (comment.id)}
            <FeedComment
                {comment}
                postId={post.id}
                commentData={commentData?.parentId == comment.id ||
                commentData?.commentId == comment.id
                    ? commentData
                    : null}
            />
        {/each}
    {/if}
    {#if !showAllComments && post.parentCommentsCount > INITIAL_COMMENTS_TO_SHOW && currentPage == 1 && !loading}
        <button
            on:click|preventDefault={() => (showAllComments = !showAllComments)}
            class={'btn-reset w-full text-right text-link cursor-pointer hover:text-link-hover hover:underline'}
        >
            {t.feed_post.see_more_comments({}, $lang)}
        </button>
    {/if}
    {#if !loading && currentPage < nbPages && showAllComments}
        <button
            on:click|preventDefault={() => {
                fetchMore({
                    variables: {
                        id: post.id,
                        page: currentPage + 1,
                        maxPerPage: COMMENTS_MAX_PER_PAGE,
                    },
                    updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                        let results = [
                            ...previousQueryResult.post.comments.results,
                            ...fetchMoreResult.post.comments.results,
                        ];
                        results = uniqBy(results, 'id');
                        fetchMoreResult.post.comments.results = results;
                        return fetchMoreResult;
                    },
                });
            }}
            class={'btn-reset w-full text-right text-link cursor-pointer hover:text-link-hover hover:underline'}
        >
            {t.feed_post.see_more_comments({}, $lang)}
        </button>
    {/if}

    {#if loading && post.commentsCount > 0}
        <FeedCommentLoader />
    {/if}
</div>
